import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { Loader } from "../components/atoms/Loader";
import ShowError from "../components/atoms/ShowError";
import { initZoho } from "../util/zohoUtil";
import { getRecordJourney, LOG_MODULE, FIELDS } from "../APICalling/logApi";
import { getRelatedListSetting } from "../APICalling/settingApi";
import { EXTENSION_IDENTIFIER } from "../config/serviceConstant";
import { normaliseLog } from "../components/PageComponent/Logs/logHelpers";
import { Shell, EmptyState } from "../components/PageComponent/RelatedList/Common";
import JourneyTimeline from "../components/PageComponent/Journey/JourneyTimeline";

const ZOHO = window.ZOHO;

// Field that holds a record's Shopify ID. The source of truth is the per-module
// related-list setting (settingSchema.IdField); this is the fallback, matching
// RelatedList.js. Used to OR-match logs by Shopify Record ID on normal modules.
const DEFAULT_SHOPIFY_ID_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_ID_EXT`;
const DEFAULT_SHOPIFY_SHOP_URL_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_Shop_URL`;

export default function Journey() {
  const [loading, setLoading] = useState(true);
  const [extensionData, setExtensionData] = useState(null);

  // PageLoad context — Zoho SDK tells us which module/record we're embedded in.
  const [entity, setEntity] = useState(null);
  const [entityId, setEntityId] = useState(null);

  const [fetching, setFetching] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");
  const [storeDomain, setStoreDomain] = useState("");

  // ── Initialise Zoho & read PageLoad context ──
  useEffect(() => {
    initZoho((err, data) => {
      if (err || !data) {
        setLoading(false);
        return;
      }
      setExtensionData(data);
      setEntity(data.entity || null);
      setEntityId(data.entityId || null);
      setLoading(false);
    });
  }, []);

  // ── Resolve the match keys, then fetch + assemble the journey ──
  //
  // The match strategy flips on the host module:
  //   • Shopify Logs module → the current record IS a log, so its own ID is
  //     useless. Read this log's Module_Record_ID / Shopify_Record_ID FIELD
  //     VALUES and find every sibling log sharing them.
  //   • Any other module → match logs by this record's own ID (Module_Record_ID)
  //     OR its stored Shopify ID (Shopify_Record_ID).
  useEffect(() => {
    if (!extensionData || !entity || !entityId) return;
    if (!extensionData.orgId || !extensionData.apiKey || !extensionData.dataCenterUrl) return;

    let cancelled = false;
    setFetching(true);
    setError("");

    (async () => {
      try {
        let moduleRecordId = "";
        let shopifyRecordId = "";
        let shopFieldValue = "";

        if (entity === LOG_MODULE) {
          // Embedded on a single Shopify Log → pivot off its field values.
          const resp = await ZOHO.CRM.API.getRecord({ Entity: LOG_MODULE, RecordID: entityId });
          const log = resp?.data?.[0] || null;
          moduleRecordId = log?.[FIELDS.MODULE_RECORD_ID] || "";
          shopifyRecordId = log?.[FIELDS.SHOPIFY_RECORD_ID] || "";
        } else {
          // Normal module → record ID is the entity ID; Shopify ID comes from
          // the record's configured Shopify-ID field.
          const auth = {
            orgId: extensionData.orgId,
            apiKey: extensionData.apiKey,
            dataCenterUrl: extensionData.dataCenterUrl,
            shop: "",
          };
          const [setting, resp] = await Promise.all([
            getRelatedListSetting(auth, entity),
            ZOHO.CRM.API.getRecord({ Entity: entity, RecordID: entityId }),
          ]);
          const record = resp?.data?.[0] || null;
          const schema = setting?.settingSchema || {};
          const idField = schema.IdField || DEFAULT_SHOPIFY_ID_FIELD;
          const shopField = schema.shopUrlField || DEFAULT_SHOPIFY_SHOP_URL_FIELD;

          moduleRecordId = entityId;
          shopifyRecordId = record?.[idField] || "";
          shopFieldValue = record?.[shopField] || "";
        }

        const { records } = await getRecordJourney(ZOHO, { moduleRecordId, shopifyRecordId });

        // Normalise, then sort oldest → newest for a chronological journey.
        const normalised = (records || [])
          .map(normaliseLog)
          .sort((a, b) => {
            const ta = a.createdTime ? new Date(a.createdTime).getTime() : 0;
            const tb = b.createdTime ? new Date(b.createdTime).getTime() : 0;
            return ta - tb;
          });

        if (cancelled) return;
        setEvents(normalised);
        // Prefer the record's own shop; otherwise borrow it from a log entry.
        setStoreDomain(shopFieldValue || normalised.find((e) => e.shop)?.shop || "");
      } catch (err) {
        if (cancelled) return;
        console.error("[Journey] fetch error:", err);
        setError("Failed to load this record's sync journey. Please try again.");
        setEvents([]);
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [extensionData, entity, entityId]);

  // ── Render guards ──

  if (loading) return <Loader />;

  if (!extensionData?.orgId || !extensionData?.apiKey || !extensionData?.dataCenterUrl) {
    return <ShowError />;
  }

  if (fetching) {
    return <Shell storeDomain={storeDomain} loading={true} />;
  }

  if (error) {
    return (
      <Shell storeDomain={storeDomain}>
        <EmptyState icon="⚠️" tone="danger" title="Couldn't load the journey" message={error} />
      </Shell>
    );
  }

  if (events.length === 0) {
    return (
      <Shell storeDomain={storeDomain}>
        <EmptyState
          icon="🛤️"
          tone="info"
          title="No sync history yet"
          message="This record hasn't been part of any Shopify ↔ Zoho sync yet. Once it syncs in either direction, every step will appear here as a chronological timeline."
        />
      </Shell>
    );
  }

  return (
    <Shell storeDomain={storeDomain}>
      <Box sx={{ mb: "10px" }}>
        <Box sx={{ fontSize: 17, fontWeight: 700, color: "#0f172a", fontFamily: '"Inter", system-ui, sans-serif' }}>
          Sync Journey
        </Box>
        <Box sx={{ fontSize: 14, color: "#71717a", mt: "2px" }}>
          Every Shopify ↔ Zoho CRM step recorded for this record, oldest to newest. Expand a step to read its notes.
        </Box>
      </Box>
      <JourneyTimeline events={events} />
    </Shell>
  );
}
